<template>
  <div class="involved-card mb-3">
    <!-- Cabeçalho accordion -->
    <div
      class="involved-card__header"
      @click="expanded = !expanded"
    >
      <div class="involved-card__avatar">
        {{ initials }}
      </div>
      <div class="involved-card__info">
        <span class="involved-card__name">
          <span
            v-if="involvedData.unidentified"
            class="badge badge-warning mr-1"
            style="font-size: 10px"
          >Não identificado</span>
          <span
            class="badge badge-light-secondary mr-1"
            style="font-size: 10px"
          >{{ entityTypeLabel }}</span>
          <span
            v-if="role"
            class="badge badge-light-primary mr-1"
            style="font-size: 10px"
          >{{ role.label }}</span>
          {{ displayName }}
        </span>
        <span
          v-if="entitySubInfo"
          class="involved-card__meta"
        >{{ entitySubInfo }}</span>
      </div>
      <div class="d-flex align-items-center">
        <span
          v-if="activeDestination"
          class="badge badge-light-info mr-1"
          style="font-size: 10px"
        >
          <feather-icon
            icon="MapPinIcon"
            size="10"
            class="mr-25"
          />
          Destino: {{ activeDestination.establishment ? activeDestination.establishment.establishment_name : '—' }}
        </span>
        <span
          v-if="involved.outcome"
          class="badge badge-light-success mr-1"
          style="font-size: 10px"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="10"
            class="mr-25"
          />
          Desfecho registrado
        </span>
        <button
          v-if="entityType === 'person' && $hasAbility('envolvido.gerenciar')"
          type="button"
          class="involved-card__edit-btn mr-1"
          title="Editar envolvido"
          @click.stop="showEditModal = true"
        >
          <feather-icon
            icon="EditIcon"
            size="14"
          />
        </button>
        <feather-icon
          :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          size="16"
          class="text-muted"
        />
      </div>
    </div>

    <OccurrenceInvolvedEditModal
      :visible.sync="showEditModal"
      :involved="involved"
      @edited="$emit('edited')"
    />

    <!-- Corpo accordion -->
    <div
      v-show="expanded"
      class="involved-card__body"
    >
      <!-- Resumo da natureza (veículo, objeto, imóvel, celular) -->
      <div
        v-if="summaryComponent"
        class="mini-tab-content mb-2"
      >
        <component
          :is="summaryComponent"
          :data="involvedData"
        />
      </div>

      <!-- Mini-abas dirigidas pelos painéis do papel (BD) -->
      <div
        v-if="panels.length"
        class="involved-mini-tabs"
      >
        <button
          v-for="p in panels"
          :key="p.key"
          class="involved-mini-tab"
          :class="{ active: activePanelKey === p.key }"
          @click="activePanelKey = p.key"
        >
          <feather-icon
            :icon="p.icon"
            size="13"
            class="mr-25"
          />
          {{ p.label }}
        </button>
      </div>

      <FormRenderer
        v-if="currentCustomForm"
        :key="currentCustomForm.id"
        :form="currentCustomForm"
        :occurrence-involved-id="involved.id"
        @saved="loadCustomForms"
      />
      <keep-alive v-else>
        <component
          :is="currentPanel"
          v-if="currentPanel"
          :occurrence-id="occurrenceId"
          :involved-id="clinicalEntityId"
          :occurrence-involved-id="involved.id"
          :discipline-id="disciplineId"
          :initial-outcomes="activePanelKey === 'outcome' ? (involved.involved_outcomes || []) : undefined"
          :initial-destinations="activePanelKey === 'destination' ? localDestinations : undefined"
          :occurrence-latitude="occurrenceLatitude"
          :occurrence-longitude="occurrenceLongitude"
          @outcome-saved="$emit('outcome-saved')"
          @destination-saved="localDestinations = $event"
        />
      </keep-alive>

      <div
        v-if="!panels.length"
        class="text-center text-muted py-2"
      >
        Nenhum painel configurado para este papel.
      </div>
    </div>
  </div>
</template>

<script>
import VehicleSummary from '@/components/involved/VehicleSummary.vue'
import ObjectSummary from '@/components/involved/ObjectSummary.vue'
import PropertySummary from '@/components/involved/PropertySummary.vue'
import PhoneSummary from '@/components/involved/PhoneSummary.vue'
import FormRenderer from '@/components/involved/panels/form-renderer/FormRenderer.vue'
import OccurrenceInvolvedEditModal from '@/components/involved/OccurrenceInvolvedEditModal.vue'
import { PANEL_COMPONENTS, PANEL_META, CLINICAL_FALLBACK } from '@/components/involved/panels'
import { formattedAge, genderLabel } from '@/utils/age'

export default {
  name: 'InvolvedClinicalCard',
  components: {
    VehicleSummary,
    ObjectSummary,
    PropertySummary,
    PhoneSummary,
    FormRenderer,
    OccurrenceInvolvedEditModal,
  },
  props: {
    involved: { type: Object, required: true },
    occurrenceId: { type: [Number, String], required: true },
    disciplineId: { type: [Number, String], default: null },
    openPanelKey: { type: String, default: null },
    occurrenceLatitude: { type: [Number, String], default: null },
    occurrenceLongitude: { type: [Number, String], default: null },
  },

  data() {
    return {
      expanded: false,
      activePanelKey: null,
      // Formulários (form builder) aplicáveis a este envolvido — cada um vira
      // sua própria mini-aba dinâmica (ver `panels`/`currentCustomForm`),
      // em vez de um único painel "Formulários" com sub-abas internas.
      customForms: [],
      showEditModal: false,
      // Cópia local reativa do histórico de destinos, para o badge do
      // cabeçalho atualizar na hora após salvar/cancelar, sem depender de um
      // reload completo da lista de envolvidos (a prop `involved` só é
      // atualizada quando o pai recarrega).
      localDestinations: [...(this.involved.involved_destinations || [])],
    }
  },
  computed: {
    activeDestination() {
      return this.localDestinations.find(d => !d.cancelled_at) || null
    },
    involvedData() {
      return this.involved.entity || this.involved.involved || {}
    },
    clinicalEntityId() {
      const entity = this.involved.entity || this.involved.involved || {}
      return this.involved.entity_id || entity.id || null
    },
    role() {
      return this.involved.role || null
    },
    entityType() {
      return this.involved.entity_type || 'person'
    },
    // Painéis (conteúdos do acordeão) deste envolvido, vindos do BD.
    // Preferência: painéis EFETIVOS resolvidos no backend (involved.panels,
    // já considerando override por disciplina) → painéis-default do papel → fallback.
    // Sem o filtro de PANEL_COMPONENTS ainda — `custom_forms` não tem componente
    // próprio (vira N abas dinâmicas, ver `panels` abaixo).
    rawPanels() {
      const effective = this.involved.panels
      const rolePanels = this.role && this.role.panels
      if (effective && effective.length) {
        return effective.map(p => ({ key: p.key, label: p.label, icon: p.icon }))
      }
      if (rolePanels && rolePanels.length) {
        return rolePanels.map(p => ({ key: p.key, label: p.label, icon: p.icon }))
      }
      // Fallback (occurrence_involveds legado sem papel): pessoa = clínico; demais = desfecho.
      const keys = this.entityType === 'person' ? CLINICAL_FALLBACK : ['outcome']
      return keys.map(k => ({ key: k, label: PANEL_META[k].label, icon: PANEL_META[k].icon }))
    },
    hasCustomFormsPanel() {
      return this.rawPanels.some(p => p.key === 'custom_forms')
    },
    // Abas finais: painéis estáticos (com componente registrado) + uma aba
    // dinâmica por FormDefinition aplicável (form builder), no lugar do antigo
    // painel único "Formulários" com sub-abas internas.
    panels() {
      const staticPanels = this.rawPanels
        .filter(p => p.key !== 'custom_forms')
        .filter(p => PANEL_COMPONENTS[p.key])
      if (!this.hasCustomFormsPanel) return staticPanels
      const formTabs = this.customForms.map(f => ({
        key: `form_${f.id}`,
        label: f.name,
        icon: 'LayoutIcon',
      }))
      return [...staticPanels, ...formTabs]
    },
    currentPanel() {
      if (!this.activePanelKey || this.activePanelKey.startsWith('form_')) return null
      return PANEL_COMPONENTS[this.activePanelKey] || null
    },
    currentCustomForm() {
      if (!this.activePanelKey || !this.activePanelKey.startsWith('form_')) return null
      const id = Number(this.activePanelKey.slice('form_'.length))
      return this.customForms.find(f => f.id === id) || null
    },
    entityTypeLabel() {
      const map = {
        person: 'Pessoa', vehicle: 'Veículo', object: 'Objeto', property: 'Imóvel', phone: 'Celular',
      }
      return map[this.entityType] || 'Envolvido'
    },
    entitySubInfo() {
      const d = this.involvedData || {}
      if (this.entityType === 'person') {
        const parts = []
        const age = formattedAge(d)
        if (age) parts.push(age)
        const gender = genderLabel(d.gender)
        if (gender) parts.push(gender)
        return parts.join(' · ')
      }
      if (this.entityType === 'object') {
        const map = {
          arma: 'Arma', eletronico: 'Eletrônico', documento: 'Documento', joia: 'Joia/Valor', outro: 'Outro',
        }
        return map[d.object_type] || d.object_type || ''
      }
      if (this.entityType === 'vehicle') {
        const map = {
          carro: 'Carro', moto: 'Moto', caminhao: 'Caminhão', onibus: 'Ônibus', outro: 'Outro',
        }
        return map[d.vehicle_type] || d.vehicle_type || [d.brand, d.model].filter(Boolean).join(' ') || ''
      }
      if (this.entityType === 'property') {
        const map = {
          casa: 'Casa', apartamento: 'Apartamento', comercio: 'Comércio', terreno: 'Terreno', outro: 'Outro',
        }
        return map[d.property_type] || d.property_type || ''
      }
      if (this.entityType === 'phone') {
        return d.carrier || d.owner_name || ''
      }
      return ''
    },
    summaryComponent() {
      const map = {
        vehicle: 'VehicleSummary', object: 'ObjectSummary', property: 'PropertySummary', phone: 'PhoneSummary',
      }
      return map[this.entityType] || null
    },
    displayName() {
      const d = this.involvedData
      if (!d) return '—'
      if (this.entityType === 'vehicle') {
        return d.plate || [d.brand, d.model].filter(Boolean).join(' ') || d.apparent_description || 'Veículo'
      }
      if (this.entityType === 'object') {
        return d.description || d.object_type || 'Objeto'
      }
      if (this.entityType === 'property') {
        return d.address || d.description || d.property_type || 'Imóvel'
      }
      if (this.entityType === 'phone') {
        return d.number || d.owner_name || 'Celular'
      }
      if (d.unidentified) return d.apparent_description || '—'
      return d.name || '—'
    },
    initials() {
      const name = this.displayName
      if (!name || name === '—') return '?'
      return name.split(' ').slice(0, 2).map(w => w[0]).join('')
        .toUpperCase()
    },
  },
  watch: {
    openPanelKey(key) {
      if (!key) return
      this.expanded = true
      this.activePanelKey = key
    },
  },

  created() {
    this.activePanelKey = this.openPanelKey
      || (this.panels.length ? this.panels[0].key : null)

    if (this.openPanelKey) this.expanded = true
    if (this.hasCustomFormsPanel) this.loadCustomForms()
  },

  methods: {
    // Carrega os FormDefinitions aplicáveis a este envolvido (form builder) e
    // vira uma aba dinâmica por formulário. Rechamado após salvar uma resposta
    // para ressincronizar (ver @saved no FormRenderer).
    async loadCustomForms() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}involved_forms`, {
          params: { occurrence_involved_id: this.involved.id, context: 'web' },
        })
        this.customForms = res.data.data || []
      } catch (e) {
        this.customForms = []
      }
    },
  },
}
</script>

<style src="@/components/involved/panels/panel-styles.css"></style>

<style scoped>
/* ── Accordion card (shell) ───────────────────────────────────── */
.involved-card {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  overflow: hidden;
}

.involved-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  background: #f8f7ff;
  border-left: 4px solid #7367f0;
  transition: background 0.15s;
  user-select: none;
}

.involved-card__header:hover {
  background: #f0effe;
}

.involved-card__edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #7367f0;
  cursor: pointer;
  transition: background 0.15s;
}

.involved-card__edit-btn:hover {
  background: rgba(115, 103, 240, 0.12);
}

.involved-card__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.involved-card__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.involved-card__name {
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
}

.involved-card__meta {
  font-size: 11px;
  color: #b9b9c3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.involved-card__body {
  padding: 16px;
  border-top: 1px solid #ebe9f1;
}

.involved-mini-tabs {
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid #ebe9f1;
  margin-bottom: 16px;
}

.involved-mini-tab {
  background: none;
  border: none;
  padding: 8px 14px;
  font-size: 12px;
  color: #b9b9c3;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
  display: flex;
  align-items: center;
}

.involved-mini-tab:hover {
  color: #7367f0;
}

.involved-mini-tab.active {
  color: #7367f0;
  border-bottom-color: #7367f0;
}

/* ── Dark theme (shell) ───────────────────────────────────────── */
.dark-layout .involved-card {
  border-color: #3b4253;
}

.dark-layout .involved-card__header {
  background: #161d31;
}

.dark-layout .involved-card__header:hover {
  background: #1a2340;
}

.dark-layout .involved-card__name {
  color: #d0d2d6;
}

.dark-layout .involved-card__body {
  border-top-color: #3b4253;
}

.dark-layout .involved-mini-tabs {
  border-bottom-color: #3b4253;
}
</style>
